// src/pages/research/ResearchTopic.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    InputAdornment,
    Tooltip,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stepper,
    Step,
    StepLabel,
    Fade,
    Autocomplete
} from '@mui/material';
import {
    Add as AddIcon,
    Search,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    Delete,
    Close as CloseIcon,
    FilterAlt as FilterIcon,
    Today as TodayIcon,
    Send as SendIcon,
    CheckCircle,
    Cancel,
    Schedule,
    Flag,
    Timeline,
    RateReview,
    Feedback,
    Approval,
    Verified,
    Pending,
    Article,
    Description,
    LocalOffer,
    Lightbulb,
    School,
    DateRange,
    Update,
    CheckBoxOutlineBlank,
    ExpandMore,
    ClearAll,
    CloudUpload,
    Download,
    Category,
    QuestionAnswer,
    Science,
    MenuBook,
    Psychology
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIResearchTopicStore,
    APIResearchTopicUpdate,
    APIResearchTopicDelete,
    APIResearchTopicSubmit,
    APIResearchTopicUpdateStatus,
    APIResearchTopicByStudent,
    APISpecialtyByInstitution,
    APIResearchTopicCheckExists
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    getUserDetails,
    recordsCount,
    recordsPerPage
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
// NOTE: The component file is still named StudentBar.js
// but it now renders "Researcher Portal" as its visible title.
import StudentBar from '../../components/StudentBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Helper function to parse JSON fields safely
const parseJsonField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    
    try {
        let parsed = JSON.parse(field);
        while (typeof parsed === 'string') {
            try {
                parsed = JSON.parse(parsed);
            } catch {
                break;
            }
        }
        return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
        try {
            let cleaned = field;
            if (cleaned.startsWith('"') && cleaned.endsWith('"')) {
                cleaned = cleaned.slice(1, -1);
            }
            cleaned = cleaned.replace(/\\"/g, '"');
            cleaned = cleaned.replace(/\\\\/g, '\\');
            const parsed = JSON.parse(cleaned);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e2) {
            console.warn('Failed to parse JSON field:', field, e2);
            return [];
        }
    }
};

const stringifyForBackend = (value) => {
    if (!value || (Array.isArray(value) && value.length === 0)) return null;
    return JSON.stringify(value);
};

const topicStatusOptions = [
    { id: 'draft', name: 'Draft', color: 'default', icon: <Description /> },
    { id: 'submitted', name: 'Submitted', color: 'info', icon: <SendIcon /> },
    { id: 'under_review', name: 'Under Review', color: 'warning', icon: <Schedule /> },
    { id: 'revision_required', name: 'Revision Required', color: 'warning', icon: <RateReview /> },
    { id: 'approved', name: 'Approved', color: 'success', icon: <CheckCircle /> },
    { id: 'rejected', name: 'Rejected', color: 'error', icon: <Cancel /> },
    { id: 'superseded', name: 'Superseded', color: 'secondary', icon: <Update /> }
];

const reviewStatusOptions = [
    { id: 'pending_review', name: 'Pending Review', color: 'warning', icon: <Pending /> },
    { id: 'review_in_progress', name: 'Review In Progress', color: 'info', icon: <Timeline /> },
    { id: 'review_completed', name: 'Review Completed', color: 'success', icon: <CheckBoxOutlineBlank /> }
];

const approvalStatusOptions = [
    { id: 'pending', name: 'Pending', color: 'warning', icon: <Pending /> },
    { id: 'approved', name: 'Approved', color: 'success', icon: <Verified /> },
    { id: 'rejected_with_feedback', name: 'Rejected with Feedback', color: 'error', icon: <Feedback /> },
    { id: 'conditional_approval', name: 'Conditional Approval', color: 'info', icon: <Approval /> }
];

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const ResearchTopic = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [pageState, setPageState] = useState({ pageData: [], specialtiesData: [] });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeStep, setActiveStep] = useState(0);
    const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
    const [feedbackInput, setFeedbackInput] = useState('');
    const [uploadFile, setUploadFile] = useState(null);
    const [existingFileUrl, setExistingFileUrl] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [specialtySearchInput, setSpecialtySearchInput] = useState('');
    const [checkingDuplicate, setCheckingDuplicate] = useState(false);
    const isMounted = useRef(true);

    const [formData, setFormData] = useState({
        topic_title: '',
        topic_description: '',
        specialty_id: '',
        specialty_name: '',
        keywords: [],
        research_questions: [],
        objectives: [],
        methodology_brief: '',
        literature_review_summary: '',
        expected_contribution: ''
    });

    const [keywordInput, setKeywordInput] = useState('');
    const [questionInput, setQuestionInput] = useState('');
    const [objectiveInput, setObjectiveInput] = useState('');

    const [filters, setFilters] = useState({
        topic_status: '',
        review_status: '',
        approval_status: '',
        dateFrom: '',
        dateTo: ''
    });

    const [errors, setErrors] = useState({
        topic_title: false,
        topic_description: false,
        specialty_id: false,
        duplicate_topic: false,
        duplicate_message: ''
    });

    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [openStatusUpdate, setOpenStatusUpdate] = useState(false);

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchData();
        fetchSpecialtiesData();
        
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Real-time duplicate checking effect
    useEffect(() => {
        // Debounced duplicate check for real-time validation
        const checkDuplicate = async () => {
            if (postStatus === "ADD" && 
                formData.topic_title && 
                formData.topic_title.trim().length > 5 && 
                formData.specialty_id) {
                
                setCheckingDuplicate(true);
                try {
                    const response = await apiService.post(APIResearchTopicCheckExists, {
                        topic_title: formData.topic_title.trim(),
                        specialty_id: formData.specialty_id,
                        student_id: getUserDetails("Id")
                    });
                    
                    if (response.success) {
                        // Topic is available
                        setErrors(prev => ({ 
                            ...prev, 
                            duplicate_topic: false,
                            duplicate_message: ''
                        }));
                    }
                } catch (error) {
                    // Check if the error response contains duplicate info
                    if (error.response?.status === 409 || error.statusCode === 409) {
                        const existingTopic = error.data || error.response?.data?.data;
                        setErrors(prev => ({ 
                            ...prev, 
                            duplicate_topic: true,
                            duplicate_message: `Similar topic exists: "${existingTopic?.existing_topic_title || 'Unknown'}" (${existingTopic?.existing_topic_status || 'Draft'})`
                        }));
                    } else {
                        // Other errors - don't block the user
                        setErrors(prev => ({ 
                            ...prev, 
                            duplicate_topic: false,
                            duplicate_message: ''
                        }));
                    }
                } finally {
                    setCheckingDuplicate(false);
                }
            } else {
                // Reset duplicate error when conditions aren't met
                setErrors(prev => ({ 
                    ...prev, 
                    duplicate_topic: false,
                    duplicate_message: ''
                }));
            }
        };

        // Debounce timer - wait 800ms after user stops typing
        const debounceTimer = setTimeout(checkDuplicate, 800);
        return () => clearTimeout(debounceTimer);
    }, [formData.topic_title, formData.specialty_id, postStatus]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const studentId = getUserDetails("Id");
            const result = await apiService.listById(APIResearchTopicByStudent, studentId);

            if (result.success && isMounted.current) {
                const topics = (Array.isArray(result.data) ? result.data : []).map(topic => ({
                    ...topic,
                    keywords: parseJsonField(topic.keywords),
                    research_questions: parseJsonField(topic.research_questions),
                    objectives: parseJsonField(topic.objectives)
                })).reverse();
                setPageState(prev => ({ ...prev, pageData: topics }));
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchSpecialtiesData = async () => {
        try {
            const result = await apiService.listByInstitutionAndId(APISpecialtyByInstitution);
            if (result.success && isMounted.current) {
                const specialties = Array.isArray(result.data) ? result.data.reverse() : [];
                setPageState(prev => ({
                    ...prev,
                    specialtiesData: specialties
                }));
            } else {
                console.error("Failed to load specialties:", result.message);
            }
        } catch (error) {
            console.error("Error fetching specialties:", error);
            showToast("Error", "Failed to load specialties", "error");
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const extension = file.name.split('.').pop().toLowerCase();
            const allowedExtensions = ['doc', 'docx'];
            if (allowedExtensions.includes(extension)) {
                setUploadFile(file);
            } else {
                showToast("Error", "Please upload a Word document (.doc or .docx) only", "error");
            }
        }
    };

    const handleAddKeyword = () => {
        if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
            setFormData(prev => ({
                ...prev,
                keywords: [...prev.keywords, keywordInput.trim()]
            }));
            setKeywordInput('');
        }
    };

    const handleRemoveKeyword = (keyword) => {
        setFormData(prev => ({
            ...prev,
            keywords: prev.keywords.filter(k => k !== keyword)
        }));
    };

    const handleAddQuestion = () => {
        if (questionInput.trim()) {
            setFormData(prev => ({
                ...prev,
                research_questions: [...prev.research_questions, questionInput.trim()]
            }));
            setQuestionInput('');
        }
    };

    const handleRemoveQuestion = (question) => {
        setFormData(prev => ({
            ...prev,
            research_questions: prev.research_questions.filter(q => q !== question)
        }));
    };

    const handleAddObjective = () => {
        if (objectiveInput.trim()) {
            setFormData(prev => ({
                ...prev,
                objectives: [...prev.objectives, objectiveInput.trim()]
            }));
            setObjectiveInput('');
        }
    };

    const handleRemoveObjective = (objective) => {
        setFormData(prev => ({
            ...prev,
            objectives: prev.objectives.filter(o => o !== objective)
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: false }));
        }
        // Reset duplicate error when user types
        if (name === 'topic_title') {
            setErrors(prev => ({ ...prev, duplicate_topic: false, duplicate_message: '' }));
        }
    };

    const handleSpecialtySelect = (event, selectedSpecialty) => {
        if (selectedSpecialty) {
            setFormData(prev => ({
                ...prev,
                specialty_id: selectedSpecialty.id,
                specialty_name: selectedSpecialty.name
            }));
            setErrors(prev => ({ ...prev, specialty_id: false }));
        } else {
            setFormData(prev => ({
                ...prev,
                specialty_id: '',
                specialty_name: ''
            }));
        }
    };

    const handlefilterOptions = () => setFilterOptions(true);
    const handleCloseOptions = () => setFilterOptions(false);

    const handleOpenAdd = () => {
        setPostStatus("ADD");
        setActiveStep(0);
        setFormData({
            topic_title: '',
            topic_description: '',
            specialty_id: '',
            specialty_name: '',
            keywords: [],
            research_questions: [],
            objectives: [],
            methodology_brief: '',
            literature_review_summary: '',
            expected_contribution: ''
        });
        setKeywordInput('');
        setQuestionInput('');
        setObjectiveInput('');
        setUploadFile(null);
        setExistingFileUrl(null);
        setSpecialtySearchInput('');
        setErrors({ 
            topic_title: false, 
            topic_description: false, 
            specialty_id: false,
            duplicate_topic: false,
            duplicate_message: ''
        });
        setOpenAdd(true);
    };

    const handleCloseAdd = () => setOpenAdd(false);

    const handleOpenEdit = (record) => {
        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");
        setActiveStep(0);
        setFormData({
            topic_title: record.topic_title || '',
            topic_description: record.topic_description || '',
            specialty_id: record.specialty_id || '',
            specialty_name: record.specialty_name || '',
            keywords: parseJsonField(record.keywords),
            research_questions: parseJsonField(record.research_questions),
            objectives: parseJsonField(record.objectives),
            methodology_brief: record.methodology_brief || '',
            literature_review_summary: record.literature_review_summary || '',
            expected_contribution: record.expected_contribution || ''
        });
        setUploadFile(null);
        setExistingFileUrl(record.file_url || null);
        setSpecialtySearchInput(record.specialty_name || '');
        setErrors({ 
            topic_title: false, 
            topic_description: false, 
            specialty_id: false,
            duplicate_topic: false,
            duplicate_message: ''
        });
        setOpenAdd(true);
    };

    const handleOpenView = (record) => {
        const parsedRecord = {
            ...record,
            keywords: parseJsonField(record.keywords),
            research_questions: parseJsonField(record.research_questions),
            objectives: parseJsonField(record.objectives)
        };
        setSelectedRecord(parsedRecord);
        setOpenView(true);
    };

    const handleCloseView = () => setOpenView(false);

    const handleOpenDelete = (record) => {
        setSelectedRecord(record);
        setUpdateId(record.id);
        setOpenDelete(true);
    };

    const handleCloseDelete = () => setOpenDelete(false);

    const handleOpenStatusUpdate = (record) => {
        setSelectedRecord(record);
        setFeedbackInput(record.supervisor_feedback || '');
        setOpenStatusUpdate(true);
    };

    const handleCloseStatusUpdate = () => setOpenStatusUpdate(false);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters(prev => ({ ...prev, dateFrom: today, dateTo: today }));
        handleCloseOptions();
    };

    const clearFilters = () => {
        setFilters({
            topic_status: '',
            review_status: '',
            approval_status: '',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    };

    const handleSubmitTopic = async (id) => {
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('updateId', id);
            formDataToSend.append('user_id', getUserDetails("Id"));

            const result = await apiService.update(APIResearchTopicSubmit, formDataToSend);

            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                fetchData();
            }
        } catch (error) {
            showToast("Error", error.response?.data?.message || "Failed to submit topic", "error");
        }
    };

    const handleUpdateStatus = async (record, newStatus) => {
        setStatusUpdateLoading(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('user_id', getUserDetails("Id"));
            formDataToSend.append('updateId', record.id);
            formDataToSend.append('topic_status', newStatus);
            if (feedbackInput) {
                formDataToSend.append('supervisor_feedback', feedbackInput);
            }

            const result = await apiService.update(APIResearchTopicUpdateStatus, formDataToSend);

            if (result && result.success && isMounted.current) {
                const statusName = topicStatusOptions.find(s => s.id === newStatus)?.name || newStatus;
                showToast("Success", `Topic status updated to ${statusName}`, "success");
                fetchData();
                handleCloseStatusUpdate();
            }
        } catch (error) {
            showToast("Error", error.response?.data?.message || "Failed to update status", "error");
        } finally {
            setStatusUpdateLoading(false);
        }
    };

    const validateForm = () => {
        let isValid = true;
        
        if (!formData.topic_title.trim()) {
            setErrors(prev => ({ ...prev, topic_title: true }));
            isValid = false;
        }
        if (!formData.topic_description.trim()) {
            setErrors(prev => ({ ...prev, topic_description: true }));
            isValid = false;
        }
        if (!formData.specialty_id) {
            setErrors(prev => ({ ...prev, specialty_id: true }));
            isValid = false;
        }
        if (errors.duplicate_topic) {
            showToast("Duplicate Topic", errors.duplicate_message, "warning");
            isValid = false;
        }
        if (!isValid) {
            showToast("Validation Error", "Please fix all errors before proceeding", "warning");
        }
        return isValid;
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        
        if (postStatus === "ADD" && !uploadFile) {
            showToast("Error", "Please upload a Word document (.doc or .docx) for your research topic", "error");
            return;
        }

        // Double-check for duplicate before saving (if ADD mode)
        if (postStatus === "ADD") {
            try {
                const checkResponse = await apiService.post(APIResearchTopicCheckExists, {
                    topic_title: formData.topic_title.trim(),
                    specialty_id: formData.specialty_id,
                    student_id: getUserDetails("Id")
                });
                
                // If success, topic is available - proceed
                if (!checkResponse.success) {
                    // If there's a conflict, show the error
                    if (checkResponse.statusCode === 409) {
                        const existingTopic = checkResponse.data;
                        showToast(
                            "Duplicate Topic Found",
                            `A topic with a similar title already exists: "${existingTopic?.existing_topic_title}" (Status: ${existingTopic?.existing_topic_status})`,
                            "warning"
                        );
                        setErrors(prev => ({ 
                            ...prev, 
                            duplicate_topic: true,
                            duplicate_message: `Similar topic exists: "${existingTopic?.existing_topic_title}" (${existingTopic?.existing_topic_status})`
                        }));
                        return;
                    }
                    // Other errors - continue to let the server handle it
                }
            } catch (error) {
                // If the check fails, we proceed anyway - server will validate
                console.warn('Duplicate check failed, proceeding with save:', error);
            }
        }

        setIsSubmitting(true);
        
        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('student_id', getUserDetails("Id"));
        formDataToSend.append('university_id', getUserDetails("universityId"));
        formDataToSend.append('topic_title', formData.topic_title);
        formDataToSend.append('topic_description', formData.topic_description);
        formDataToSend.append('specialty_id', formData.specialty_id);
        formDataToSend.append('keywords', stringifyForBackend(formData.keywords));
        formDataToSend.append('research_questions', stringifyForBackend(formData.research_questions));
        formDataToSend.append('objectives', stringifyForBackend(formData.objectives));
        formDataToSend.append('methodology_brief', formData.methodology_brief);
        formDataToSend.append('literature_review_summary', formData.literature_review_summary);
        formDataToSend.append('expected_contribution', formData.expected_contribution);
        
        if (uploadFile) {
            formDataToSend.append('file', uploadFile);
        }

        if (postStatus === "UPDATE") {
            formDataToSend.append('updateId', updateId);
        }

        try {
            let result;
            if (postStatus === "ADD") {
                result = await apiService.create(APIResearchTopicStore, formDataToSend);
            } else {
                result = await apiService.update(APIResearchTopicUpdate, formDataToSend);
            }

            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                handleCloseAdd();
                fetchData();
            } else if (result && !result.success) {
                // Check if it's a duplicate conflict
                if (result.statusCode === 409) {
                    const existingTopic = result.data;
                    showToast(
                        "Duplicate Topic Found",
                        `A topic with a similar title already exists: "${existingTopic?.existing_topic_title}" (Status: ${existingTopic?.existing_topic_status})`,
                        "warning"
                    );
                    setErrors(prev => ({ 
                        ...prev, 
                        duplicate_topic: true,
                        duplicate_message: `Similar topic exists: "${existingTopic?.existing_topic_title}" (${existingTopic?.existing_topic_status})`
                    }));
                } else {
                    showToast("Error", result.message || "Operation failed", "error");
                }
            }
        } catch (error) {
            // Check if error is a duplicate topic conflict
            if (error.response?.status === 409 || error.statusCode === 409) {
                const existingTopic = error.data || error.response?.data?.data;
                showToast(
                    "Duplicate Topic Found",
                    `A topic with a similar title already exists: "${existingTopic?.existing_topic_title}" (Status: ${existingTopic?.existing_topic_status})`,
                    "warning"
                );
                setErrors(prev => ({ 
                    ...prev, 
                    duplicate_topic: true,
                    duplicate_message: `Similar topic exists: "${existingTopic?.existing_topic_title}" (${existingTopic?.existing_topic_status})`
                }));
            } else {
                showToast("Error", error.response?.data?.message || "Operation failed", "error");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        formDataToSend.append('updateId', updateId);

        try {
            const result = await apiService.delete(APIResearchTopicDelete, formDataToSend);
            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                handleCloseDelete();
                fetchData();
            }
        } catch (error) {
            showToast("Error", error.response?.data?.message || "Failed to delete", "error");
        }
    };

    const handleDownloadFile = (fileUrl) => {
        if (fileUrl) {
            window.open(fileUrl, '_blank');
        }
    };

    const filteredData = pageState.pageData.filter(record => {
        const matchesSearch = searchTerm === '' ||
            record.topic_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.topic_id?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesTopicStatus = !filters.topic_status || record.topic_status === filters.topic_status;
        const matchesReviewStatus = !filters.review_status || record.review_status === filters.review_status;
        const matchesApprovalStatus = !filters.approval_status || record.approval_status === filters.approval_status;

        let matchesDate = true;
        if (filters.dateFrom && record.submitted_date) {
            matchesDate = matchesDate && record.submitted_date.split('T')[0] >= filters.dateFrom;
        }
        if (filters.dateTo && record.submitted_date) {
            matchesDate = matchesDate && record.submitted_date.split('T')[0] <= filters.dateTo;
        }

        return matchesSearch && matchesTopicStatus && matchesReviewStatus && matchesApprovalStatus && matchesDate;
    });

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const steps = ['Topic Information', 'Research Details', 'Methodology'];

    if (loading) {
        return <LoadingState message="Loading Research Topics..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchData} title="Unable to Load Research Topics" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer>
                <Box sx={{backgroundColor: 'white',borderRadius: 3 }}>
                    <StudentBar notificationCount={5} />
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2, 
                            flexWrap: 'wrap',
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography variant="h6" sx={{
                                fontWeight: 'bold',
                                color: theme.palette.primary.main,
                                flexShrink: 0
                            }}>
                                Research Topics ({recordsCount(pageState.pageData.length)})
                            </Typography>

                            <Box sx={{ 
                                display: 'flex', 
                                gap: 1, 
                                flexShrink: 0,
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: { xs: 'center', sm: 'flex-start' }
                            }}>
                                <Button
                                    variant="contained"
                                    color="warning"
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenAdd}
                                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                                >
                                    New Research Topic
                                </Button>
                            
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<FilterIcon />}
                                    onClick={handlefilterOptions}
                                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                                >
                                    View Options
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="info"
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchData}
                                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                                >
                                    Refresh
                                </Button>
                            </Box>

                            <Paper sx={{
                                width: { xs: '100%', sm: '300px' },
                                p: '2px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                marginLeft: { xs: 0, sm: 'auto' },
                                borderRadius: '8px',
                                backgroundColor: theme.palette.grey[50],
                            }}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Search topics..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search sx={{ color: theme.palette.grey[600] }} />
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            '& fieldset': { border: 'none' },
                                        }
                                    }}
                                    size="small"
                                />
                            </Paper>
                        </Box>

                        {(filters.topic_status || filters.review_status || filters.approval_status || filters.dateFrom || filters.dateTo) && (
                            <Box sx={{ 
                                display: 'flex', 
                                gap: 1, 
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                mt: 2,
                                pt: 2,
                                borderTop: '1px solid',
                                borderColor: 'divider'
                            }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                    Active filters:
                                </Typography>
                                {filters.topic_status && (
                                    <Chip
                                        label={`Status: ${topicStatusOptions.find(s => s.id === filters.topic_status)?.name || filters.topic_status}`}
                                        onDelete={() => setFilters(prev => ({ ...prev, topic_status: '' }))}
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                                {filters.review_status && (
                                    <Chip
                                        label={`Review: ${reviewStatusOptions.find(s => s.id === filters.review_status)?.name || filters.review_status}`}
                                        onDelete={() => setFilters(prev => ({ ...prev, review_status: '' }))}
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                                {filters.approval_status && (
                                    <Chip
                                        label={`Approval: ${approvalStatusOptions.find(s => s.id === filters.approval_status)?.name || filters.approval_status}`}
                                        onDelete={() => setFilters(prev => ({ ...prev, approval_status: '' }))}
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                                {filters.dateFrom && (
                                    <Chip
                                        label={`From: ${new Date(filters.dateFrom).toLocaleDateString()}`}
                                        onDelete={() => setFilters(prev => ({ ...prev, dateFrom: '' }))}
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                                {filters.dateTo && (
                                    <Chip
                                        label={`To: ${new Date(filters.dateTo).toLocaleDateString()}`}
                                        onDelete={() => setFilters(prev => ({ ...prev, dateTo: '' }))}
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                                <Button
                                    size="small"
                                    onClick={clearFilters}
                                    startIcon={<ClearAll />}
                                    sx={{ ml: 1 }}
                                >
                                    Clear All
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Box>

                {/* Topics Table */}
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                    <Paper elevation={2} sx={{ 
                        borderRadius: 3, 
                        overflow: 'auto',
                        backgroundColor: 'white',
                    }}>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: { xs: 600, sm: 700, md: 800 } }}>
                                <TableHead>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        '& th': {
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' }
                                        }
                                    }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>Supervisor</TableCell>
                                        <TableCell>Title</TableCell>
                                        <TableCell>File</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Review</TableCell>
                                        <TableCell>Submitted</TableCell>
                                        <TableCell>Assigned</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((record, index) => (
                                        <TableRow
                                            key={record.id}
                                            hover
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Chip label={page * rowsPerPage + index + 1} color="primary" size="small" />
                                            </TableCell>
                                            <TableCell>
                                                {record.supervisor_name || 'Not Assigned'}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Article fontSize="small" color="primary" />
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {record.topic_title?.substring(0, 50)}...
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {record.file_url && (
                                                    <Tooltip title="Download Word Document">
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => handleDownloadFile(record.file_url)}
                                                            color="primary"
                                                        >
                                                            <Article />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={topicStatusOptions.find(s => s.id === record.topic_status)?.icon}
                                                    label={topicStatusOptions.find(s => s.id === record.topic_status)?.name || record.topic_status}
                                                    color={topicStatusOptions.find(s => s.id === record.topic_status)?.color}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={reviewStatusOptions.find(s => s.id === record.review_status)?.icon}
                                                    label={reviewStatusOptions.find(s => s.id === record.review_status)?.name || record.review_status}
                                                    color={reviewStatusOptions.find(s => s.id === record.review_status)?.color}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>{formatCreatedAtDate(record.submitted_date)}</TableCell>
                                            <TableCell>{record.assigned_date}</TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="View">
                                                    <IconButton onClick={() => handleOpenView(record)} color="primary" size="small">
                                                        <ViewIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                
                                                {record.topic_status === 'draft' && (
                                                    <>
                                                        <Tooltip title="Edit">
                                                            <IconButton onClick={() => handleOpenEdit(record)} color="secondary" size="small">
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton onClick={() => handleOpenDelete(record)} color="error" size="small">
                                                                <Delete />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                                
                                                {record.topic_status === 'draft' && (
                                                    <Tooltip title="Submit for Review">
                                                        <IconButton onClick={() => { handleSubmitTopic(record.id); }} color="success" size="small">
                                                            <SendIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                
                                                {record.topic_status !== 'approved' && record.topic_status !== 'rejected' && record.topic_status !== 'draft' && (
                                                    <Tooltip title="Update Status">
                                                        <IconButton onClick={() => handleOpenStatusUpdate(record)} color="warning" size="small">
                                                            <Schedule />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ mt: 2 }}
                        />

                        {filteredData.length === 0 && (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                py: 8,
                                color: 'text.secondary'
                            }}>
                                <Search sx={{ fontSize: 60, mb: 2 }} />
                                <Typography variant="h6">No research topics found</Typography>
                                <Typography variant="body2">Try adjusting your search query</Typography>
                            </Box>
                        )}
                    </Paper>
                </motion.div>

                {/* Filter Options Dialog */}
                <Dialog
                    open={filterOptions}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="sm"
                    disableEscapeKeyDown
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseOptions();
                        }
                    }}
                >
                    <DialogTitle sx={{ textAlign: 'center' }}>Filter Options</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<RefreshIcon />}
                                onClick={clearFilters}
                                sx={{ textTransform: 'none' }}
                            >
                                Show All Topics
                            </Button>

                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<TodayIcon />}
                                onClick={applyTodayFilter}
                                sx={{ textTransform: 'none' }}
                            >
                                Show Today's Submissions
                            </Button>

                            <Divider>OR</Divider>

                            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                                Filter by Status
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Topic Status</InputLabel>
                                        <Select
                                            name="topic_status"
                                            value={filters.topic_status}
                                            onChange={handleFilterChange}
                                            label="Topic Status"
                                        >
                                            <MenuItem value="">All Status</MenuItem>
                                            {topicStatusOptions.map((status) => (
                                                <MenuItem key={status.id} value={status.id}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {status.icon}
                                                        {status.name}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Review Status</InputLabel>
                                        <Select
                                            name="review_status"
                                            value={filters.review_status}
                                            onChange={handleFilterChange}
                                            label="Review Status"
                                        >
                                            <MenuItem value="">All Status</MenuItem>
                                            {reviewStatusOptions.map((status) => (
                                                <MenuItem key={status.id} value={status.id}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {status.icon}
                                                        {status.name}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Approval Status</InputLabel>
                                        <Select
                                            name="approval_status"
                                            value={filters.approval_status}
                                            onChange={handleFilterChange}
                                            label="Approval Status"
                                        >
                                            <MenuItem value="">All Status</MenuItem>
                                            {approvalStatusOptions.map((status) => (
                                                <MenuItem key={status.id} value={status.id}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {status.icon}
                                                        {status.name}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Divider>OR</Divider>

                            <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
                                Filter by Date Range
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="From Date"
                                        type="date"
                                        name="dateFrom"
                                        value={filters.dateFrom}
                                        onChange={handleFilterChange}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="To Date"
                                        type="date"
                                        name="dateTo"
                                        value={filters.dateTo}
                                        onChange={handleFilterChange}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button color="error" onClick={handleCloseOptions} variant="contained">Close</Button>
                        <Button onClick={handleCloseOptions} color="primary" variant="contained">Apply Filters</Button>
                    </DialogActions>
                </Dialog>

                {/* Add/Edit Dialog */}
                <Dialog
                    open={openAdd}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseAdd();
                        }
                    }}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="md"
                    scroll="paper"
                    disableEscapeKeyDown
                >
                    <DialogTitle sx={{ textAlign: 'center' }}>
                        <b>{postStatus === "ADD" ? "Submit New Research Topic" : "Edit Research Topic"}</b>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                                {steps.map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>

                            {/* Step 1: Topic Information */}
                            {activeStep === 0 && (
                                <Fade in={activeStep === 0}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Topic Title *"
                                                name="topic_title"
                                                value={formData.topic_title}
                                                onChange={handleInputChange}
                                                error={errors.topic_title || errors.duplicate_topic}
                                                helperText={
                                                    errors.duplicate_topic ? errors.duplicate_message : 
                                                    (errors.topic_title ? "Topic title is required" : 
                                                    "Enter a unique title for your research topic")
                                                }
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Lightbulb />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: checkingDuplicate && (
                                                        <InputAdornment position="end">
                                                            <CircularProgress size={20} />
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Topic Description *"
                                                name="topic_description"
                                                value={formData.topic_description}
                                                onChange={handleInputChange}
                                                multiline
                                                rows={4}
                                                error={errors.topic_description}
                                                helperText={errors.topic_description ? "Topic description is required" : ""}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Description />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                options={pageState.specialtiesData}
                                                loading={loading}
                                                value={pageState.specialtiesData.find(s => s.id === formData.specialty_id) || null}
                                                onChange={handleSpecialtySelect}
                                                inputValue={specialtySearchInput}
                                                onInputChange={(event, newInputValue) => {
                                                    setSpecialtySearchInput(newInputValue);
                                                }}
                                                getOptionLabel={(option) => `${option.name} ${option.specialty_code ? `(${option.specialty_code})` : ''}`}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                renderOption={(props, option) => (
                                                    <li {...props}>
                                                        <Box>
                                                            <Typography variant="body1">
                                                                <strong>{option.name}</strong>
                                                            </Typography>
                                                            {option.specialty_code && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Code: {option.specialty_code}
                                                                </Typography>
                                                            )}
                                                            {option.description && (
                                                                <Typography variant="caption" color="text.secondary" display="block">
                                                                    {option.description.substring(0, 100)}...
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </li>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Research Specialty *"
                                                        required
                                                        error={!!errors.specialty_id}
                                                        helperText={errors.specialty_id ? "Please select a research specialty" : "Select the research area/specialty for your topic"}
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            startAdornment: (
                                                                <>
                                                                    <InputAdornment position="start">
                                                                        <Category color="action" />
                                                                    </InputAdornment>
                                                                    {params.InputProps?.startAdornment}
                                                                </>
                                                            ),
                                                            endAdornment: (
                                                                <>
                                                                    {loading && <CircularProgress color="inherit" size={20} />}
                                                                    {params.InputProps?.endAdornment}
                                                                </>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                Upload Research Document (Word) *
                                            </Typography>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                component="label"
                                                startIcon={<CloudUpload />}
                                                sx={{ mb: 1 }}
                                            >
                                                {uploadFile ? 'Change File' : (postStatus === "ADD" ? 'Select Word Document (.doc, .docx) *' : 'Upload New Version (Optional)')}
                                                <input
                                                    type="file"
                                                    hidden
                                                    accept=".doc,.docx"
                                                    onChange={handleFileChange}
                                                />
                                            </Button>
                                            {uploadFile && (
                                                <Chip
                                                    icon={<Article />}
                                                    label={uploadFile.name}
                                                    onDelete={() => setUploadFile(null)}
                                                    color="primary"
                                                    size="small"
                                                />
                                            )}
                                            {postStatus === "UPDATE" && existingFileUrl && !uploadFile && (
                                                <Box sx={{ mt: 1 }}>
                                                    <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                                                        Current file: 
                                                        <Button 
                                                            size="small" 
                                                            onClick={() => handleDownloadFile(existingFileUrl)}
                                                            startIcon={<Download />}
                                                        >
                                                            Download
                                                        </Button>
                                                    </Typography>
                                                </Box>
                                            )}
                                            {postStatus === "UPDATE" && !uploadFile && !existingFileUrl && (
                                                <Typography variant="caption" color="warning.main">
                                                    No file uploaded. Please upload a Word document.
                                                </Typography>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Fade>
                            )}

                            {/* Step 2: Research Details */}
                            {activeStep === 1 && (
                                <Fade in={activeStep === 1}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                Keywords
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    placeholder="Add keyword..."
                                                    value={keywordInput}
                                                    onChange={(e) => setKeywordInput(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword()}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <LocalOffer />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                                <Button variant="outlined" onClick={handleAddKeyword}>Add</Button>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {formData.keywords.map((keyword, idx) => (
                                                    <Chip
                                                        key={idx}
                                                        label={keyword}
                                                        onDelete={() => handleRemoveKeyword(keyword)}
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                Research Questions
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    placeholder="Add research question..."
                                                    value={questionInput}
                                                    onChange={(e) => setQuestionInput(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleAddQuestion()}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <QuestionAnswer />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                                <Button variant="outlined" onClick={handleAddQuestion}>Add</Button>
                                            </Box>
                                            {formData.research_questions.map((question, idx) => (
                                                <Paper key={idx} sx={{ p: 2, mb: 1, bgcolor: 'grey.50' }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2">{question}</Typography>
                                                        <IconButton size="small" onClick={() => handleRemoveQuestion(question)}>
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Paper>
                                            ))}
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                Objectives
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    placeholder="Add objective..."
                                                    value={objectiveInput}
                                                    onChange={(e) => setObjectiveInput(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleAddObjective()}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Flag />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                                <Button variant="outlined" onClick={handleAddObjective}>Add</Button>
                                            </Box>
                                            {formData.objectives.map((objective, idx) => (
                                                <Paper key={idx} sx={{ p: 2, mb: 1, bgcolor: 'grey.50' }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Typography variant="body2">{objective}</Typography>
                                                        <IconButton size="small" onClick={() => handleRemoveObjective(objective)}>
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Paper>
                                            ))}
                                        </Grid>
                                    </Grid>
                                </Fade>
                            )}

                            {/* Step 3: Methodology */}
                            {activeStep === 2 && (
                                <Fade in={activeStep === 2}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Research Problem"
                                                name="literature_review_summary"
                                                value={formData.literature_review_summary}
                                                onChange={handleInputChange}
                                                multiline
                                                rows={3}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <MenuBook />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Methodology Brief"
                                                name="methodology_brief"
                                                value={formData.methodology_brief}
                                                onChange={handleInputChange}
                                                multiline
                                                rows={3}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Science />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Expected Contribution"
                                                name="expected_contribution"
                                                value={formData.expected_contribution}
                                                onChange={handleInputChange}
                                                multiline
                                                rows={3}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Psychology />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Fade>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                                <Button 
                                    disabled={activeStep === 0} 
                                    onClick={() => setActiveStep(prev => prev - 1)}
                                >
                                    Back
                                </Button>
                                {activeStep === steps.length - 1 ? (
                                    <Button 
                                        variant="contained" 
                                        onClick={handleSave}
                                        disabled={isSubmitting}
                                        startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                                    >
                                        {isSubmitting ? 'Saving...' : (postStatus === "ADD" ? "Save Topic" : "Update Topic")}
                                    </Button>
                                ) : (
                                    <Button variant="contained" onClick={() => setActiveStep(prev => prev + 1)}>
                                        Next
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button color="error" onClick={handleCloseAdd} variant="contained" disabled={isSubmitting}>
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* View Dialog */}
                <Dialog
                    open={openView}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseView();
                        }
                    }}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="md"
                    disableEscapeKeyDown
                >
                    <DialogTitle sx={{ textAlign: 'center' }}>
                        <b>Research Topic Details</b>
                    </DialogTitle>
                    <DialogContent>
                        {selectedRecord && (
                            <Box sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                    <Typography variant="h6" color="primary">
                                        {selectedRecord.topic_id}
                                    </Typography>
                                    <Box>
                                        <Chip
                                            icon={topicStatusOptions.find(s => s.id === selectedRecord.topic_status)?.icon}
                                            label={topicStatusOptions.find(s => s.id === selectedRecord.topic_status)?.name}
                                            color={topicStatusOptions.find(s => s.id === selectedRecord.topic_status)?.color}
                                            sx={{ mr: 1 }}
                                        />
                                        <Chip
                                            icon={reviewStatusOptions.find(s => s.id === selectedRecord.review_status)?.icon}
                                            label={reviewStatusOptions.find(s => s.id === selectedRecord.review_status)?.name}
                                            color={reviewStatusOptions.find(s => s.id === selectedRecord.review_status)?.color}
                                            variant="outlined"
                                        />
                                    </Box>
                                </Box>

                                <Accordion defaultExpanded>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Lightbulb color="primary" />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                Topic Information
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <b>Title:</b> {selectedRecord.topic_title}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <b>Description:</b> {selectedRecord.topic_description}
                                                </Typography>
                                            </Grid>
                                            {selectedRecord.specialty_name && (
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle2">
                                                        <b>Research Specialty:</b> 
                                                        <Chip 
                                                            label={selectedRecord.specialty_name} 
                                                            size="small" 
                                                            icon={<Category />}
                                                            sx={{ ml: 1 }}
                                                        />
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {selectedRecord.file_url && (
                                                <Grid item xs={12}>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<Article />}
                                                        onClick={() => handleDownloadFile(selectedRecord.file_url)}
                                                        size="small"
                                                    >
                                                        Download Research Document
                                                    </Button>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Science color="secondary" />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                Research Details
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <b>Keywords:</b>
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                                    {selectedRecord.keywords?.map((k, i) => (
                                                        <Chip key={i} label={k} size="small" />
                                                    ))}
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <b>Research Questions:</b>
                                                </Typography>
                                                <ul>
                                                    {selectedRecord.research_questions?.map((q, i) => (
                                                        <li key={i}>
                                                            <Typography variant="body2">{q}</Typography>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <b>Objectives:</b>
                                                </Typography>
                                                <ul>
                                                    {selectedRecord.objectives?.map((o, i) => (
                                                        <li key={i}>
                                                            <Typography variant="body2">{o}</Typography>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <MenuBook color="info" />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                Methodology & Contribution
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <b>Methodology:</b>
                                                </Typography>
                                                <Typography variant="body2">
                                                    {selectedRecord.methodology_brief || 'Not provided'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <b>Literature Review:</b>
                                                </Typography>
                                                <Typography variant="body2">
                                                    {selectedRecord.literature_review_summary || 'Not provided'}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2">
                                                    <b>Expected Contribution:</b>
                                                </Typography>
                                                <Typography variant="body2">
                                                    {selectedRecord.expected_contribution || 'Not provided'}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                {selectedRecord.supervisor_feedback && (
                                    <Accordion>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Feedback color="warning" />
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
                                                    Supervisor Feedback
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography variant="body2">{selectedRecord.supervisor_feedback}</Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                <Accordion>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <DateRange color="action" />
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                Dates
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Typography variant="subtitle2">
                                                    <b>Submitted:</b> {formatCreatedAtDate(selectedRecord.submitted_date)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="subtitle2">
                                                    <b>Last Modified:</b> {formatCreatedAtDate(selectedRecord.last_modified_date)}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button color="error" onClick={handleCloseView} variant="contained">Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={openDelete}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseDelete();
                        }
                    }}
                    TransitionComponent={Transition}
                    disableEscapeKeyDown
                >
                    <DialogTitle sx={{ textAlign: 'center' }}>Delete Research Topic</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ textAlign: 'center', mb: 2 }}>
                            Are you sure you want to delete this research topic?
                        </Typography>
                        {selectedRecord && (
                            <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                                {selectedRecord.topic_title}
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    ID: {selectedRecord.topic_id}
                                </Typography>
                                <Box sx={{ mt: 2 }}>
                                    <Chip
                                        icon={topicStatusOptions.find(s => s.id === selectedRecord.topic_status)?.icon}
                                        label={topicStatusOptions.find(s => s.id === selectedRecord.topic_status)?.name}
                                        color={topicStatusOptions.find(s => s.id === selectedRecord.topic_status)?.color}
                                        size="small"
                                    />
                                </Box>
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button color="error" onClick={handleCloseDelete} variant="contained">Cancel</Button>
                        <Button onClick={handleDelete} color="primary" variant="contained">Confirm Delete</Button>
                    </DialogActions>
                </Dialog>

                {/* Status Update Dialog */}
                <Dialog
                    open={openStatusUpdate}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseStatusUpdate();
                        }
                    }}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="sm"
                    disableEscapeKeyDown
                >
                    <DialogTitle sx={{ textAlign: 'center' }}>
                        <b>Update Topic Status</b>
                    </DialogTitle>
                    <DialogContent>
                        {selectedRecord && (
                            <Box sx={{ p: 2 }}>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Topic:</strong> {selectedRecord.topic_title}<br/>
                                        <strong>Current Status:</strong> {' '}
                                        <Chip
                                            icon={topicStatusOptions.find(s => s.id === selectedRecord.topic_status)?.icon}
                                            label={topicStatusOptions.find(s => s.id === selectedRecord.topic_status)?.name}
                                            color={topicStatusOptions.find(s => s.id === selectedRecord.topic_status)?.color}
                                            size="small"
                                        />
                                    </Typography>
                                </Alert>

                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 2 }}>
                                    Select New Status
                                </Typography>

                                <Grid container spacing={2}>
                                    {topicStatusOptions
                                        .filter(s => s.id !== 'draft' && s.id !== 'submitted')
                                        .map((status) => (
                                            <Grid item xs={12} sm={6} key={status.id}>
                                                <Button
                                                    fullWidth
                                                    variant={selectedRecord.topic_status === status.id ? "contained" : "outlined"}
                                                    color={status.color}
                                                    onClick={() => handleUpdateStatus(selectedRecord, status.id)}
                                                    disabled={statusUpdateLoading || selectedRecord.topic_status === status.id}
                                                    sx={{
                                                        justifyContent: 'center',
                                                        textTransform: 'none',
                                                        py: 1.5
                                                    }}
                                                >
                                                    {status.icon}
                                                    <Box sx={{ ml: 1 }}>{status.name}</Box>
                                                </Button>
                                            </Grid>
                                        ))}
                                </Grid>

                                {(selectedRecord.topic_status === 'revision_required' || selectedRecord.topic_status === 'rejected') && (
                                    <Box sx={{ mt: 3 }}>
                                        <TextField
                                            fullWidth
                                            label="Supervisor Feedback"
                                            multiline
                                            rows={3}
                                            value={feedbackInput}
                                            onChange={(e) => setFeedbackInput(e.target.value)}
                                            placeholder="Provide feedback to the researcher..."
                                        />
                                    </Box>
                                )}

                                {statusUpdateLoading && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                )}
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button color="error" onClick={handleCloseStatusUpdate} variant="contained" disabled={statusUpdateLoading}>
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default ResearchTopic;